// Consultas de las piezas del wizard: promos de la tienda, evaluación de la sesión y cupón de la venta.
import { useMemo, useSyncExternalStore } from 'react';
import { useQuery, useQueryClient, type QueryClient, type QueryKey } from '@tanstack/react-query';
import { isPromotionsConfigured } from '@/domains/retail/admin/promotions/api/promotions.client';
import { shouldRetryPromotionsQuery } from '@/domains/retail/admin/promotions/lib/promotionsFormat';
import { WIZARD_KEYS } from '@/domains/sales/wizard/api/useWizard';
import type { WizardSession } from '@/domains/sales/wizard/types';
import type { SaleAudience } from '../lib/saleAudience';
import { customerPhoneOf } from '../lib/customerPhone';
import { couponPollingDone, saleCouponPollInterval } from '../lib/saleClosing';
import type {
    EvaluateSalePromotionsResponse,
    ListStoreSalePromotionsResponse,
} from './wizardPromotionsContract';
import { wizardPromotionsClient } from './wizardPromotions.client';

export const WIZARD_PROMOTIONS_KEY = ['retail-promotions', 'wizard'] as const;

/** Lo que Promociones lee de la sesión: líneas vivas, forma de pago y teléfono del cliente. */
/** Código apartado en la venta, si hay. */
function heldCodeText(session: WizardSession | undefined): string | null {
    const line = (session?.reservedLines ?? []).find(
        (l) => l.status !== 'RELEASING' && l.discountCode,
    );
    return line?.discountCode ?? null;
}

export function saleSignature(session: WizardSession | undefined): string {
    if (!session) return 'no-session';
    const lines = (session.reservedLines ?? [])
        .filter((l) => l.status !== 'RELEASING')
        .map((l) => [l.lineId, l.sku, l.qty, l.status, l.paymentType])
        .sort((a, b) => String(a[0]).localeCompare(String(b[0])));
    return JSON.stringify([
        lines,
        session.financedLineId ?? null,
        session.paymentMethodKind,
        session.phone,
        heldCodeText(session),
    ]);
}

/** Firma de la sesión en la caché de React Query. */
export function cachedSaleSignature(sessionId: string, queryClient?: QueryClient): string {
    return saleSignature(queryClient?.getQueryData<WizardSession>(WIZARD_KEYS.session(sessionId)));
}

const noSubscription = (): (() => void) => () => undefined;

/** Firma de la sesión que se vuelve a leer cada vez que la caché cambia. */
function useSaleSignature(sessionId: string, queryClient?: QueryClient): string {
    const subscribe = useMemo(
        () =>
            queryClient
                ? (onChange: () => void) => queryClient.getQueryCache().subscribe(onChange)
                : noSubscription,
        [queryClient],
    );
    return useSyncExternalStore(subscribe, () => cachedSaleSignature(sessionId, queryClient));
}

/** Mientras llega la lectura de la venta cambiada, la de antes de la misma consulta (no de otra sesión). */
function previousOfSameQuery(baseKey: QueryKey) {
    return <T>(previous: T | undefined, previousQuery?: { queryKey: QueryKey }): T | undefined => {
        const key = previousQuery?.queryKey;
        const same = key?.length === baseKey.length + 1 && baseKey.every((k, i) => key[i] === k);
        return same ? previous : undefined;
    };
}

/** Prefijo de las promos de la tienda; invalidarlo cubre cualquier estado de la venta. */
export function storePromotionsKey(sessionId: string) {
    return [...WIZARD_PROMOTIONS_KEY, 'store', sessionId] as const;
}

/** Prefijo de la evaluación; invalidarlo cubre cualquier estado de la venta. */
export function evaluationKey(sessionId: string) {
    return [...WIZARD_PROMOTIONS_KEY, 'evaluate', sessionId] as const;
}

/** Llave de la evaluación para la venta tal como está hoy en la caché. */
export function currentEvaluationKey(sessionId: string, queryClient?: QueryClient) {
    return [...evaluationKey(sessionId), cachedSaleSignature(sessionId, queryClient)] as const;
}

/** La evaluación de la sesión con datos más recientes, sea cual sea su firma. */
function latestEvaluation(
    sessionId: string,
    queryClient: QueryClient,
): EvaluateSalePromotionsResponse | undefined {
    const latest = queryClient
        .getQueryCache()
        .findAll({ queryKey: evaluationKey(sessionId) })
        .filter((query) => query.state.data !== undefined)
        .sort((a, b) => b.state.dataUpdatedAt - a.state.dataUpdatedAt)
        .at(0);
    return latest?.state.data as EvaluateSalePromotionsResponse | undefined;
}

/** Evaluación de la venta como está hoy; mientras no llega, la última que hubo (p. ej. la de SureKeep). */
export function cachedEvaluation(
    sessionId: string,
    queryClient?: QueryClient,
): EvaluateSalePromotionsResponse | undefined {
    if (!queryClient) return undefined;
    return (
        queryClient.getQueryData<EvaluateSalePromotionsResponse>(
            currentEvaluationKey(sessionId, queryClient),
        ) ?? latestEvaluation(sessionId, queryClient)
    );
}

/** Las promos de la tienda más recientes en caché, sea cual sea la firma de la venta. */
export function cachedStorePromotions(
    sessionId: string,
    queryClient?: QueryClient,
): ListStoreSalePromotionsResponse | undefined {
    const latest = queryClient
        ?.getQueryCache()
        .findAll({ queryKey: storePromotionsKey(sessionId) })
        .filter((query) => query.state.data !== undefined)
        .sort((a, b) => b.state.dataUpdatedAt - a.state.dataUpdatedAt)
        .at(0);
    return latest?.state.data as ListStoreSalePromotionsResponse | undefined;
}

/** Promos vigentes de la tienda de la sesión; con `queryClient` no exige el proveedor. */
export function useStoreSalePromotions(sessionId: string, queryClient?: QueryClient) {
    const contextClient = useQueryClient(queryClient);
    const signature = useSaleSignature(sessionId, contextClient);
    return useQuery(
        {
            queryKey: [...storePromotionsKey(sessionId), signature],
            queryFn: () => wizardPromotionsClient.listStorePromotions(sessionId),
            enabled: isPromotionsConfigured(),
            retry: shouldRetryPromotionsQuery,
            staleTime: 60_000,
            placeholderData: previousOfSameQuery(storePromotionsKey(sessionId)),
        },
        queryClient,
    );
}

/** Regalos y promos de un equipo en el detalle, antes de que sea la ancla. */
export function useSkuSalePromotions(sessionId: string, sku: string, { enabled = true } = {}) {
    const signature = useSaleSignature(sessionId, useQueryClient());
    return useQuery({
        queryKey: [...WIZARD_PROMOTIONS_KEY, 'by-sku', sessionId, sku, signature] as const,
        queryFn: () => wizardPromotionsClient.listSkuPromotions(sessionId, sku),
        enabled: enabled && isPromotionsConfigured(),
        retry: shouldRetryPromotionsQuery,
        staleTime: 60_000,
        placeholderData: previousOfSameQuery([...WIZARD_PROMOTIONS_KEY, 'by-sku', sessionId, sku]),
    });
}

/** Promos que aplican a la venta tal como está en la sesión, con su código apartado. */
export function useSalePromotionEvaluation(sessionId: string, { enabled = true } = {}) {
    const signature = useSaleSignature(sessionId, useQueryClient());
    return useQuery({
        queryKey: [...evaluationKey(sessionId), signature],
        queryFn: ({ client }) => {
            const phone = customerPhoneOf(sessionId, client);
            const discountCode = heldCodeText(
                client.getQueryData<WizardSession>(WIZARD_KEYS.session(sessionId)),
            );
            return wizardPromotionsClient.evaluate({
                sessionId,
                ...(phone && { phone }),
                ...(discountCode && { discountCode }),
            });
        },
        enabled: enabled && isPromotionsConfigured(),
        retry: shouldRetryPromotionsQuery,
        staleTime: 15_000,
        placeholderData: previousOfSameQuery(evaluationKey(sessionId)),
    });
}

/** Promos de referido vigentes para el paso Referidos; en una apertura, solo las de abrir cuenta. */
export function useLiveReferralPromotions(sessionId: string, opening: boolean) {
    const trigger = opening ? 'ACCOUNT_OPENED' : undefined;
    return useQuery({
        queryKey: [...WIZARD_PROMOTIONS_KEY, 'referral', sessionId, trigger ?? 'ALL'] as const,
        queryFn: () => wizardPromotionsClient.listLiveReferralPromotions(sessionId, trigger),
        enabled: isPromotionsConfigured(),
        retry: shouldRetryPromotionsQuery,
        staleTime: 60_000,
    });
}

/** De dónde se lee el cupón: con `sessionId` es el cierre del wizard; sin él, una venta cerrada. */
export interface SaleCouponSource {
    sessionId?: string;
    audience?: SaleAudience;
    /** Cadena de la venta; solo la manda el admin de tenant. */
    retailerId?: string;
}

function saleCouponKey(saleId: string, source: SaleCouponSource) {
    return [
        ...WIZARD_PROMOTIONS_KEY,
        'coupon',
        saleId,
        source.sessionId ?? null,
        source.audience ?? 'STORE',
        source.retailerId ?? null,
    ] as const;
}

function fetchSaleCoupon(saleId: string, source: SaleCouponSource) {
    return source.audience === 'ADMIN'
        ? source.retailerId
            ? wizardPromotionsClient.adminSaleCoupon(saleId, source.retailerId)
            : wizardPromotionsClient.adminSaleCoupon(saleId)
        : wizardPromotionsClient.saleCoupon(saleId, source.sessionId);
}

/** Cupón y entregas de la venta; se reintenta con tope mientras se genera o el Wallet sigue en curso. */
export function useSaleCoupon(saleId: string, source: SaleCouponSource = {}) {
    return useQuery({
        queryKey: saleCouponKey(saleId, source),
        queryFn: () => fetchSaleCoupon(saleId, source),
        enabled: isPromotionsConfigured(),
        retry: shouldRetryPromotionsQuery,
        // Se sondea mientras el cupón se genera y, en el cierre, mientras el Wallet no tenga desenlace.
        staleTime: 60_000,
        refetchInterval: (query) =>
            saleCouponPollInterval(
                query.state.data,
                query.state.dataUpdateCount + query.state.errorUpdateCount,
                query.state.status === 'error',
                source.sessionId !== undefined,
            ),
    });
}

/** El cupón y si ya terminó la espera automática (tope de lecturas o una lectura fallida). */
export function useSaleCouponWithPolling(saleId: string, source: SaleCouponSource = {}) {
    const queryClient = useQueryClient();
    const query = useSaleCoupon(saleId, source);
    const state = queryClient.getQueryState(saleCouponKey(saleId, source));
    const reads = (state?.dataUpdateCount ?? 0) + (state?.errorUpdateCount ?? 0);
    const started = query.dataUpdatedAt > 0 || query.isError;
    return { query, pollingDone: started && couponPollingDone(reads, query.isError) };
}
